import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TenantFacade } from '@core/data/tenant/tenant-facade';
import { AuthFacade } from '../../../core/auth/auth-facade';
import { HttpErrorMapper } from '../../../core/http/errors/http-error.mapper';
import { SignInViewModel } from './view-models/sign-in.vm';
import { DomainErrorKind } from '../../../core/http/errors/domain-errors';
import { LoginCredentials } from '@core/auth';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { catchError, EMPTY, map, Observable, switchMap, tap } from 'rxjs';
import { ClockService } from '@core/clock/clock.service';

/**
 * SignInFacade
 *
 * Orchestrates sign-in flow:
 * 1. Validate email format
 * 2. Load tenant configuration
 * 3. Authentication (mocked)
 * 4. Navigate to dashboard
 */
@Injectable()
export class SignInFacade {
  private readonly tenantFacade = inject(TenantFacade);
  private readonly authFacade = inject(AuthFacade);
  private readonly errorMapper = inject(HttpErrorMapper);
  private readonly router = inject(Router);
  private readonly clock = inject(ClockService);

  private readonly _isSubmitting = signal(false);
  private readonly _errorMessage = signal<string | null>(null);
  private readonly _emailError = signal<string | null>(null);
  private readonly _errorKind = signal<DomainErrorKind | null>(null);

  // View model computed from state - composes tenant branding for presentation
  readonly vm: Signal<SignInViewModel> = computed(() => {
    const tenantConfig = this.tenantFacade.tenantVm();
    return {
      isSubmitting: this._isSubmitting(),
      errorMessage: this._errorMessage(),
      tenantLoading: this.tenantFacade.loading() && !tenantConfig,
      year: this.clock.tick().getFullYear()
    };
  });

  /**
   * Submit email and initiate sign-in flow.
   * If returnUrl is provided and is a valid relative path, the user will be
   * navigated there after successful authentication instead of /dashboard.
   */
  submitAuthentication(credentials: LoginCredentials, returnUrl?: string | null): void {
    this._errorMessage.set(null);
    this._emailError.set(null);
    this._errorKind.set(null);
    this._isSubmitting.set(true);

    this.authenticate(credentials)
      .pipe(
        tap(() => {
          void this.router.navigate([this.resolvePostLoginRoute(returnUrl)]);
        }),
        catchError((error) => {
          const explicitKind = this.resolveKnownErrorKind(error);
          const resolvedKind = explicitKind ?? this.errorMapper.classify(error);
          const message = this.resolveSafeErrorMessage(error, resolvedKind);

          this._errorKind.set(resolvedKind);
          this._errorMessage.set(message);

          this.authFacade.logout().subscribe();

          return EMPTY;
        }),
        finalize(() => {
          this._isSubmitting.set(false);
        })
      )
      .subscribe();
  }

  /**
   * Clear error when user starts editing
   */
  clearError(): void {
    this._errorMessage.set(null);
    this._emailError.set(null);
    this._errorKind.set(null);
  }

  authenticate(credentials: LoginCredentials): Observable<void> {
    return this.authFacade.login(credentials).pipe(
      switchMap(() => this.tenantFacade.waitForAccessibleTenants()),
      tap((tenants) => {
        if (tenants.length === 0) {
          throw new HttpErrorResponse({
            status: 403,
            statusText: 'Unauthorized',
            error: 'No Accessible Tenant Found',
          });
        }
      }),
      map(() => undefined)
    );
  }

  private resolveKnownErrorKind(error: unknown): DomainErrorKind | null {
    if (
      typeof error === 'object' &&
      error !== null &&
      'kind' in error &&
      typeof (error as { kind: unknown }).kind === 'string'
    ) {
      return (error as { kind: DomainErrorKind }).kind;
    }

    return null;
  }

  private resolveSafeErrorMessage(error: unknown, kind: DomainErrorKind): string {
    switch (kind) {
      case 'auth':
      case 'tenant':
        return 'Invalid credentials. Please check your credentials.';
      case 'forbidden':
        return 'Your account does not have access to this organization.';
      case 'server':
        return 'Service temporarily unavailable. Please try again in a few moments.';
      case 'timeout':
        return 'Request timed out. Please try again.';
      case 'network':
        return 'Unable to connect. Please check your internet connection and try again.';
      case 'validation':
        return this.errorMapper.toUserMessage(error);
      default:
        return 'An unexpected error occurred. If this persists, please contact support.';
    }
  }

  /**
   * Validate and resolve the post-login navigation target.
   *
   * Only accepts relative paths that start with '/' and do not start with '//'
   * (protocol-relative URLs that could redirect to an attacker-controlled host).
   * Falls back to '/dashboard' for any rejected value.
   */
  private resolvePostLoginRoute(returnUrl: string | null | undefined): string {
    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return '/dashboard';
    }
    return returnUrl;
  }
}
